/* eslint-disable newline-per-chained-call */
/* eslint-disable import/no-cycle */
/* eslint-disable class-methods-use-this */
import { format } from 'date-fns';
import moment from 'moment';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { DemeterDataFrequency, DemeterSymbolModel } from '../../../Generated/Raven-Demeter';
import languageService from '../Language/LanguageService';

type NumberFormatOptions = {
    type?: 'number' | 'price' | 'percent';
    locale?: string;
    zeroPlaceholder?: string | boolean;

    automaticDecimals?: boolean;
    limitTotalDigits?: number;
    minimumDecimalPlaces?: number;
    maximumDecimalPlaces?: number;

    precision?: number;
    priceKind?: 'futures' | 'currency';
    trimTrailingZerosUpTo8?: boolean;
    tinyAsZeroThreshold?: number;
    multiplier?: number;

    zeroAsEmpty?: boolean;
};

export interface ThisContext {
    x: string;
    y: number;
    point: {
        color: string;
        index: number;
    };
    series: {
        name: string;
    };
}

class FormattingService {
    // Strictly validate a localized short date string against current language's 'L' format.
    isValidDisplayDateStrict = (dateString: string | null | undefined): boolean => {
        if (typeof dateString !== 'string') {
            return false;
        }
        const trimmed = dateString.trim();
        if (trimmed.length === 0) {
            return false;
        }
        const locale = languageService.getLanguage();
        const shortFormat = moment.localeData(locale).longDateFormat('L');
        return moment(trimmed, shortFormat, true).isValid();
    };

    // Trim the string with the given characters: https://stackoverflow.com/a/55292366.
    trimStringEnd = (value: string, characterToTrim: string): string => {
        let end = value.length;

        while (end > 0 && value[end - 1] === characterToTrim) {
            end -= 1;
        }

        return end < value.length ? value.substring(0, end) : value;
    };

    // Format to a date we can use for the api.
    toApiDate = (date: Date | string, nonLocalizedDate?: boolean): string => {
        if ((typeof date === 'string' && date.indexOf('T') > 0) || nonLocalizedDate) {
            // This is already in ISO format.
            return moment(date).format('YYYY-MM-DD');
        }
        if (typeof date === 'string') {
            return moment(date, 'L').format('YYYY-MM-DD');
        }

        return format(date, 'yyyy-MM-dd');
    };

    // Parse a date string into a Date object.
    parseDate = (value: string | null | undefined, dateFormat: string | null | undefined): Date | null => {
        if (!value || value.trim() === '') {
            return null;
        }

        const parsed = moment(value, dateFormat || 'YYYY-MM-DD', true);
        return parsed.isValid() ? parsed.toDate() : null;
    };

    getWeekStartDate = (year: number, week: number, dayOffset: number | undefined = 0): Date => {
        let startOfWeek = moment().year(year).week(week).startOf('week');

        // If the start of the week is Monday (or another day), shift it back to Sunday.
        if (startOfWeek.day() !== 0) {
            startOfWeek = startOfWeek.subtract(startOfWeek.day(), 'days');
        }

        return startOfWeek.add(dayOffset, 'days').toDate();
    };

    // Formats to 'Apr 18, 2023 12:06 PM'.
    toTimestamp = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment(typeof date === 'string' ? new Date(date) : date)
            .local()
            .format('lll');
    };

    getTimeZone = () => new Date().toLocaleTimeString(languageService.getLanguage(), { timeZoneName: 'short' }).split(' ')[2];

    // Formats to 'Apr 18, 2023 12:06 PM'.
    toTimestampFromUtc = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        let newDate = date;
        if (typeof date === 'string' && !date.endsWith('Z')) {
            newDate = `${date}Z`;
        }

        return moment
            .utc(typeof newDate === 'string' ? new Date(newDate) : newDate)
            .local()
            .format('lll');
    };

    // Formats to 'Thu, 8 Jun 2023'.
    toDayOfWeekWithDayMonthYear = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment(typeof date === 'string' ? new Date(date) : date)
            .local()
            .format('ddd, ll');
    };

    // Formats to '01/10/2023' (MM/DD/YYYY) for US or '10/01/2023' (DD/MM/YYYY) for EU.
    toShortDayMonthYear = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment(typeof date === 'string' ? new Date(date) : date).format('L');
    };

    // Formats to 4 Jan 2023.
    toLongDayMonthYear = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment(typeof date === 'string' ? new Date(date) : date).format('ll');
    };

    // Formats to 'Jan 2023'.
    toMonthYear = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment(typeof date === 'string' ? new Date(date) : date).format('MMM YYYY');
    };

    // Formats to 'Jan 23' from a utc date.
    toMonthShortYearFromUtc = (date: Date | string | number | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment.utc(typeof date === 'string' ? new Date(date) : date).format('MMM YY');
    };

    // Formats to '2023' from a utc date.
    toYearFromUtc = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment.utc(typeof date === 'string' ? new Date(date) : date).format('YYYY');
    };

    // Formats to 'Jan'. The number parameter starts at 1, Jan = 1, Feb = 2.
    toMonth = (month: number): string =>
        moment()
            .month(month - 1)
            .format('MMM');

    // Formats to 'Jan' from a utc date.
    toMonthFromUtc = (date: Date | string | number | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment.utc(typeof date === 'string' ? new Date(date) : date).format('MMM');
    };

    // Formats to '8 Jun'.
    toDayMonth = (date: Date | string | null | undefined): string => {
        if (!date) {
            return '';
        }

        return moment(typeof date === 'string' ? new Date(date) : date)
            .local()
            .format('DD MMM');
    };

    // For use with the Average Seasonal charts only until other services are refactored from the old toWeekNumber method.
    toWeekNumberAverageSeasonal = (date: Date | string | null | undefined): number => {
        if (!date) {
            return 0;
        }

        const momentDate = moment(typeof date === 'string' ? new Date(date) : date);
        const year = momentDate.year();
        const currentYear = new Date().getFullYear();
        const firstDayOfYear = moment({ year, month: 0, day: 1 });
        let firstSunday: moment.Moment;
        if (firstDayOfYear.day() === 0) {
            firstSunday = firstDayOfYear.clone();
        } else {
            firstSunday = firstDayOfYear.clone().day(7);
        }

        const isCurrentYear = year === currentYear;
        if (isCurrentYear && momentDate.isBefore(firstSunday)) {
            return 1;
        }

        if (momentDate.isBefore(firstSunday)) {
            const previousYear = year - 1;
            const lastDayOfPreviousYear = moment({ year: previousYear, month: 11, day: 31 });

            return this.toWeekNumberAverageSeasonal(lastDayOfPreviousYear.toDate());
        }

        const daysSinceFirstSunday = momentDate.diff(firstSunday, 'days');
        let weekNumber = Math.floor(daysSinceFirstSunday / 7) + 1;

        // This year line and before the first FULL week of data & first day of year is not friday or later.
        if (isCurrentYear && firstDayOfYear.isBefore(firstSunday) && firstDayOfYear.day() < 5) {
            weekNumber += 1;
        }

        return weekNumber;
    };

    toWeekNumber = (date: Date | string | null | undefined): number => {
        if (!date) {
            return NaN;
        }

        const day = moment(date).startOf('day');
        const year = day.year();
        const firstDayOfYear = moment({ year, month: 0, day: 1 }).startOf('day');
        const firstSunday = firstDayOfYear.day() === 0 ? firstDayOfYear.clone() : firstDayOfYear.clone().day(7);

        if (day.isBefore(firstSunday)) {
            return 0;
        }

        const days = day.diff(firstSunday, 'days');
        return Math.floor(days / 7) + 1;
    };

    // Format to x minutes/days/months/years ago.
    toTimeAgo = (date: Date | string | null | undefined) => {
        if (!date) {
            return '';
        }

        return moment(typeof date === 'string' ? new Date(date) : date)
            .local()
            .fromNow();
    };

    private clampFractionByTotalDigits = (value: number, limitTotalDigits?: number, fallbackMax?: number) => {
        if (limitTotalDigits === undefined) {
            return { min: 0, max: fallbackMax ?? undefined };
        }

        const integerDigits = Math.trunc(Math.abs(value)).toString().length;
        const max = Math.max(0, limitTotalDigits - integerDigits);
        return { minimumFractionDigits: max, maximumFractionDigits: max };
    };

    private clampFractionDigits = (value: number | undefined, fallback: number): number => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
            return fallback;
        }

        return Math.min(20, Math.max(0, Math.trunc(numericValue)));
    };

    formatNumber = (value: number | string | null | undefined, formatOptions?: NumberFormatOptions): string => {
        const options: NumberFormatOptions = { type: 'number', ...formatOptions };
        const locale = options.locale ?? languageService.getLanguage();

        let emptyValue = '';
        if (typeof options?.zeroPlaceholder === 'string') {
            emptyValue = options.zeroPlaceholder;
        } else if (options?.zeroPlaceholder === true) {
            emptyValue = applicationConstants.TablePlaceholderZeroOrEmpty;
        }

        if (value === null || value === undefined) {
            return emptyValue;
        }

        let actualNumber: number;
        if (typeof value === 'string') {
            actualNumber = parseFloat(value);
            if (Number.isNaN(actualNumber)) {
                return emptyValue;
            }
        } else {
            actualNumber = value as number;
        }

        if (actualNumber === 0 && options.zeroAsEmpty) {
            return emptyValue;
        }

        if (options.multiplier !== undefined) {
            actualNumber *= options.multiplier;
        }

        if (options.type === 'number') {
            if (options.precision !== undefined) {
                const precision = this.clampFractionDigits(options.precision, 0);
                return actualNumber.toLocaleString(locale, {
                    minimumFractionDigits: precision,
                    maximumFractionDigits: precision,
                    style: 'decimal',
                });
            }

            if (options.limitTotalDigits !== undefined) {
                const rawFractionDigits = this.clampFractionByTotalDigits(actualNumber, options.limitTotalDigits, options.maximumDecimalPlaces);
                const minimum = this.clampFractionDigits(rawFractionDigits.minimumFractionDigits ?? 0, 0);
                const maximum = this.clampFractionDigits(rawFractionDigits.maximumFractionDigits ?? minimum, minimum);

                return actualNumber.toLocaleString(locale, {
                    minimumFractionDigits: minimum,
                    maximumFractionDigits: Math.max(maximum, minimum),
                    style: 'decimal',
                });
            }
            const autoMinimum = options.automaticDecimals ? this.getDisplayDecimalPlacesMinimum(actualNumber) : undefined;
            const minimum = this.clampFractionDigits(options.minimumDecimalPlaces ?? autoMinimum ?? 0, 0);
            const maximum = this.clampFractionDigits(options.maximumDecimalPlaces ?? applicationConstants.DefaultDisplayDecimalPlacesMaximum, minimum);

            return actualNumber.toLocaleString(locale, {
                minimumFractionDigits: minimum,
                maximumFractionDigits: Math.max(maximum, minimum),
                style: 'decimal',
            });
        }

        if (options.type === 'price') {
            const tiny = options.tinyAsZeroThreshold ?? 0.00001;
            if (Math.abs(actualNumber) < tiny) {
                return '0';
            }

            let precision: number;
            if (options.precision !== undefined) {
                precision = options.precision;
            } else if (options.priceKind === 'futures' || options.priceKind === 'currency') {
                precision = options.precision ?? 5;
            } else {
                precision = applicationConstants.DefaultDisplayDecimalPlacesMaximum;
            }

            if (options.trimTrailingZerosUpTo8) {
                precision = Math.min(precision, 8);
            }

            const clampedPrecision = this.clampFractionDigits(precision, 0);

            return actualNumber.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: clampedPrecision,
                style: 'decimal',
            });
        }

        if (options.type === 'percent') {
            const minimum = this.clampFractionDigits(options.minimumDecimalPlaces ?? 2, 2);
            const maximum = this.clampFractionDigits(options.maximumDecimalPlaces ?? 2, minimum);

            return (actualNumber / 100).toLocaleString(locale, {
                minimumFractionDigits: minimum,
                maximumFractionDigits: Math.max(maximum, minimum),
                style: 'percent',
            });
        }

        return emptyValue;
    };

    formatPriceNumber = (value: number | string | null | undefined, formatOptions?: NumberFormatOptions): string =>
        this.formatNumber(value, { type: 'price', zeroPlaceholder: true, ...formatOptions });

    formatPercentNumber = (value: number | string | null | undefined, formatOptions?: NumberFormatOptions): string =>
        this.formatNumber(value, { type: 'percent', zeroPlaceholder: true, ...formatOptions });

    getDisplayDecimalPlacesMinimum = (number: number | undefined): number => {
        if (number === undefined) {
            return 0;
        }

        const absoluteNumber = Math.abs(number);
        if (absoluteNumber >= 1000) {
            return 0;
        }

        if (absoluteNumber >= 100) {
            return 1;
        }

        if (absoluteNumber >= 10) {
            return 2;
        }

        if (absoluteNumber >= 1) {
            return 3;
        }

        if (absoluteNumber >= 0.1) {
            return 4;
        }

        return 5;
    };

    getDisplayDecimalPlacesMinimumForCharts = (number: number | undefined): number => {
        const decimalPlaces = this.getDisplayDecimalPlacesMinimum(number) - 1;

        return decimalPlaces < 0 ? 0 : decimalPlaces;
    };

    toCamelCase = (value: string): string => `${value.charAt(0).toLowerCase()}${value.slice(1)}`;

    toPascalCase = (value: string): string => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

    // Formats a futures symbol to a display name with the exchange + commodity display names.
    toDisplayName = (symbol?: Pick<DemeterSymbolModel, 'exchange' | 'subExchange' | 'displayName'>): string => {
        if (!symbol) {
            return '';
        }
        if (symbol.subExchange) {
            return `${languageService.translate('subExchange', symbol.subExchange)} ${languageService.translate(symbol.displayName)}`;
        }

        return `${languageService.translate('exchange', symbol.exchange)} ${languageService.translate(symbol.displayName)}`;
    };

    getDataFrequencyShortDisplayName = (dataFrequency: DemeterDataFrequency): string => {
        switch (dataFrequency) {
            case DemeterDataFrequency.Daily:
                return 'D';
            case DemeterDataFrequency.Weekly:
                return 'W';
            case DemeterDataFrequency.Monthly:
                return 'M';
            case DemeterDataFrequency.Yearly:
                return 'Y';
            case DemeterDataFrequency.Streaming:
                return 'S';
            default:
                return '';
        }
    };

    toShortTimeAgoSinceNow = (date: string): string => {
        if (!date) {
            return '';
        }

        const inputDate = moment(date);
        const now = moment();

        if (!inputDate.isValid()) {
            return '';
        }

        const differenceInMinutes = now.diff(inputDate, 'minutes');
        const diferencefInHours = now.diff(inputDate, 'hours');
        const differenceInDays = now.diff(inputDate, 'days');

        // Minutes (1-59)
        if (differenceInMinutes < 60) {
            return `${differenceInMinutes}m`;
        }

        // Hours (1-23)
        if (diferencefInHours < 24) {
            return `${diferencefInHours}h`;
        }

        // Days (1-6)
        if (differenceInDays < 7) {
            return `${differenceInDays}d`;
        }

        // Date format for 7+ days
        if (inputDate.year() < now.year()) {
            return inputDate.format('MMM D, YYYY');
        }

        // Same year show month and day
        return inputDate.format('MMM D');
    };

    // TODO: EVERYTHING BELOW HERE NEEDS TO BE RE-EVALUATED. DO NOT USE ANYTHING BELOW.

    toBrowserDateAndTimeFromUTC = (date: string): string => moment(new Date(`${date} UTC`)).format('YYYY-MM-DDTHH:mm:ss');

    toLongFormatDate = (date: Date): string => moment(date).format('LL');

    toLongDateAndTimeFormat = (date: Date | string): string => moment(date).format('LLLL');

    getDaysDifference = (dateString: string | null | undefined) => (dateString ? moment().diff(new Date(dateString), 'days') : undefined);

    // Linter needs posibility of returning undefined, even though it should be impossible for returned value to be undefined.
    toRegionalizedDate = (date: Date | string | null | undefined): null | string | undefined => {
        if (!date) {
            return null;
        }
        if (typeof date === 'string') {
            return date;
        }

        const returnedDateFormat = moment(date).format('L');
        return returnedDateFormat;
    };

    toMonthYearDateFormat = (date: Date | string | undefined): null | string | undefined => {
        if (!date) {
            return null;
        }
        if (typeof date === 'string') {
            return date;
        }
        const returnedDateFormat = moment(date).locale(languageService.getLanguage()).format('MM/YYYY');
        return returnedDateFormat;
    };

    getRegionalizedDateFormat = () => {
        // Converting these values for the date picker. Date picker needs to be updated to use moment.
        const longDateFormat = moment.localeData(languageService.getLanguage()).longDateFormat('L');
        return longDateFormat.replace('DD', 'dd').replace('YYYY', 'yyyy');
    };

    withParenthesisForNegatives = (number: any) => {
        if (number.value === 0 || number.value === '0' || number.value === '-') {
            return number.value;
        }

        if (number.value > 0) {
            return number.value;
        }
        return `(${Math.abs(number.value)})`;
    };

    // format tooltip dates
    formatDatesFromUTC = (timestamp: number | string, isProjectionChart?: boolean) => {
        const date = new Date(timestamp);
        if (typeof timestamp === 'number' && isProjectionChart) {
            return moment.utc(date).format('MMM YYYY');
        }
        if (typeof timestamp === 'number') {
            return moment.utc(date).format('ddd, MMM DD, HH:mm');
        }
        return timestamp;
    };

    // am using any here, because am not sure the type of 'this'
    getToolTip = (displayDecimalPlacesMinimum: number, displayDecimalPlacesMaximum: number, thisContext: ThisContext | any, isProjectionChart?: boolean) => {
        const { x, y, point, series }: ThisContext = thisContext;
        const monthOrMonthYear = this.formatDatesFromUTC(x, isProjectionChart);
        let decimalDataPoint;

        // index of 1 is always the percentage series on the projection chart
        if (isProjectionChart && thisContext?.series?.index === 1) {
            decimalDataPoint = formattingService.getDecimalDataWithAppropriateTrailingZeros(1, 1, y);
        } else if (displayDecimalPlacesMaximum !== displayDecimalPlacesMinimum) {
            decimalDataPoint = formattingService.getDecimalDataWithAppropriateTrailingZeros(displayDecimalPlacesMinimum, displayDecimalPlacesMaximum, y);
        } else {
            decimalDataPoint = formattingService.formatNumber(y, { maximumDecimalPlaces: displayDecimalPlacesMaximum });
        }
        const tooltip = `<b>${monthOrMonthYear}</b><br /><span style="color: ${point.color}">\u25CF</span> ${series.name}: <b>${decimalDataPoint}</b>`;
        return tooltip;
    };

    seperateWords = (unseperatedString: string) => unseperatedString.replace(/([A-Z])/g, ' $1').trim();

    // used when formatting the tooltip to add or take 0s after the '.'
    getDecimalDataWithAppropriateTrailingZeros = (tooltipDecimalMinimum: number, tooltipDecimalMaximum: number, value: number) => {
        let returnValue: string = value.toString();

        // get the number of places after the decimal
        const getDecimalCount = (number: number | string) => {
            const numberFromString = String(number);
            if (numberFromString.includes('.')) {
                return numberFromString.split('.')[1].length;
            }
            return 0;
        };
        const decimalCount = getDecimalCount(value);

        if (decimalCount < tooltipDecimalMinimum) {
            if (decimalCount === 0) {
                returnValue += '.';
            }
            // add 0s
            for (let i = decimalCount; i < tooltipDecimalMinimum; i += 1) {
                returnValue += '0';
            }
        } else if (decimalCount > tooltipDecimalMaximum) {
            returnValue = Number(returnValue).toFixed(tooltipDecimalMaximum);
        } else if (decimalCount > tooltipDecimalMinimum) {
            // determine if there are 0s to get rid of
            for (let i = decimalCount; i < tooltipDecimalMinimum; i += 1) {
                if (returnValue[i] === '0') {
                    returnValue.slice(0, -1);
                    // eslint-disable-next-line no-continue
                    continue;
                }
                break;
            }
        }

        return parseFloat(returnValue).toLocaleString(languageService.getLanguage(), {
            minimumFractionDigits: tooltipDecimalMinimum,
            maximumFractionDigits: tooltipDecimalMaximum,
        });
    };
}

const formattingService = new FormattingService();

export default formattingService;
